/**
 * The stored Live / Auto-refresh / Off choice, resolved against this session.
 *
 * Three surfaces share one stored preference (`display:live-mode`) — the
 * transactions explorer, the case feed and the overview activity widget — and
 * each wrote the same three lines around it: read the pref, then coerce a stored
 * `"live"` to auto-refresh where Live cannot run. Those three copies read the
 * BUILD flag, so a plan without server streaming would have kept a Live switch
 * that does nothing. This is that path, once, over `useServerStreaming()`.
 *
 * The stored value is never rewritten: an operator who chose Live keeps that
 * choice for the session (or plan) where it can run.
 */
import { effectiveLiveMode, useDisplayPref } from "@authe/ui-core";
import type { LiveMode } from "@authe/fe-commons/display";
import { displayPrefs } from "./prefs";
import { useServerStreaming } from "./serverStreaming";

export interface LiveModePref {
  /** The stored choice — what the control shows (coerced by the control when hidden) and writes. */
  mode: LiveMode;
  setMode: (mode: LiveMode) => void;
  /** What the surface should RUN: a stored `"live"` becomes `"interval"` wherever Live cannot run. */
  effectiveMode: LiveMode;
  /** Server streaming is part of this session. Gate live feeds and indicators on this. */
  serverStreaming: boolean;
  /** Live can run on THIS surface: server streaming AND the surface's own condition. */
  liveAvailable: boolean;
  /** Remove the Live option (`LiveModeControl` / `TransactionTable` `liveHidden`). */
  liveHidden: boolean;
}

/**
 * @param liveHere the surface's own condition for Live (e.g. the explorer only
 *   streams the caller's own scopes). Unrelated to the plan: when false the
 *   Live option stays, disabled; when the session has no server streaming it is
 *   removed.
 */
export function useLiveModePref(liveHere = true): LiveModePref {
  const { available } = useServerStreaming();
  const [mode, setMode] = useDisplayPref<LiveMode>(
    () => displayPrefs.getLiveMode(),
    (next) => displayPrefs.setLiveMode(next),
  );
  const liveAvailable = available && liveHere;
  return {
    mode,
    setMode,
    effectiveMode: effectiveLiveMode(mode, liveAvailable),
    serverStreaming: available,
    liveAvailable,
    liveHidden: !available,
  };
}
